// src/components/SystemInterface.js

import React, { useState, useEffect, useRef } from 'react';
import Terminal from './Terminal';

/**
 * SystemInterface
 * ---------------
 * Renders a typed terminal interface using "initialMessages", each of which
 * can be typed (type: "stream") or instantly displayed (type: "bracket", "image", etc.).
 * Optimized for performance and clarity.
 */
const CONTINUE_BUTTON_TEXT = '[ CONTINUE TO ARCHIVE ]';
const DOT_ANIMATION_INTERVAL = 500;
const DEFAULT_CURSOR_DURATION = 800;
const DEFAULT_TYPING_SPEED = 20;
const MIN_DELAY = 1000;

export default function SystemInterface({ initialMessages, theme = 'union-terminal' }) {
  const [displayedContent, setDisplayedContent] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showCursor, setShowCursor] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [cursorBlinking, setCursorBlinking] = useState(false);

  // Guard to prevent effect from re-running
  const effectRan = useRef(false);

  // Timeout references
  const cursorTimeoutRef = useRef(null);
  const typingIntervalRef = useRef(null);

  // Message index tracker
  const currentMessageIndex = useRef(0);

  // Reference for Terminal scrolling
  const terminalRef = useRef(null);

  // Cache for restructuring results to avoid recomputation
  const structureCache = useRef(new Map());

  // Memoized helper functions using useRef to prevent recreating on each render
  const helpersRef = useRef({
    clearAllTimeouts: () => {
      if (cursorTimeoutRef.current) clearTimeout(cursorTimeoutRef.current);
      if (typingIntervalRef.current) clearTimeout(typingIntervalRef.current);
    },

    ensureVisibility: () => {
      if (terminalRef.current?.scrollIntoView) {
        terminalRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end'
        });
      }
    },

    restructureContentForDots: (contentArray) => {
      const cacheKey = JSON.stringify(contentArray);
      if (structureCache.current.has(cacheKey)) {
        return structureCache.current.get(cacheKey);
      }

      if (contentArray.length < 2) return null;

      const dotParts = contentArray.filter(part => 
        typeof part.text === 'string' && part.text.trim().match(/^\.+$/)
      );
      
      const bracketParts = contentArray.filter(part => 
        typeof part.text === 'string' && 
        (part.text.includes('[') || part.text.includes(']'))
      );

      if (dotParts.length > 0 && bracketParts.length > 0) {
        const initialParts = contentArray.filter(part => {
          if (typeof part.text !== 'string') return true;
          const isDot = part.text.trim().match(/^\.+$/);
          const isBracket = part.text.includes('[') || part.text.includes(']');
          return !isDot && !isBracket;
        });

        const result = { initialParts, animatedParts: dotParts, bracketParts };
        structureCache.current.set(cacheKey, result);
        return result;
      }

      return null;
    },

shouldAnimatePart: (part) => {
  if (typeof part.text !== 'string') return false;
  
  // Check for dot sequences
  if (part.text.trim().match(/^\.+$/)) return true;
  
  // Check for current special message patterns
  return part.text.includes('Welcome.') ||
         part.text.includes(' You are now accessing previously restricted creative portfolio records,') ||
         part.text.includes(' classified by the Union as cognitively unsafe.') ||
         part.text.includes(' Which usually means it made somebody uncomfortable.') ||
         part.text.includes('Back again?') ||
         part.text.includes(' Good.') ||
         part.text.includes(' You know where everything is.') ||
         part.text.includes(' No need to go through the whole song and dance again....') ||
         part.text.includes(' A SIGHT FOR SORE EYES TO THE BLIND, WOULD B..') ||
         part.text.includes(' Aw..') ||
         part.text.includes(' for fucks sake.') ||
         part.text.includes('I had a thought a minute ago.') ||
         part.text.includes(' Terrific thing.') ||
         part.text.includes(' Sharp little bastard.') ||
         part.text.includes(' Gone now,') ||
         part.text.includes(' obviously.') ||
         part.text.includes(' Probably wandered off while I was pretending not to fall asleep.') ||
         part.text.includes(' Have a look around before it comes back.') ||
         part.text.includes('Had to disappear.') ||
         part.text.includes(' The Union sent someone with pants on.') ||
         part.text.includes(' I don’t wait around for that sort of energy.') ||
         part.text.includes(' Left you the system though.') ||
         part.text.includes(' Poke about.');
},

    buildHtml: (parts, prefix = '') => {
      let html = prefix;
      parts.forEach(part => {
        if (typeof part.text === 'string') {
          html += part.className ? 
            `<span class="${part.className}">${part.text}</span>` : 
            part.text;
        }
      });
      return html;
    }
  });

  useEffect(() => {
    if (effectRan.current) return;
    effectRan.current = true;

    const { clearAllTimeouts, ensureVisibility, restructureContentForDots, shouldAnimatePart, buildHtml } = helpersRef.current;

    function finishTyping(cursorDuration) {
      setIsTyping(false);
      setCursorBlinking(true);

      cursorTimeoutRef.current = setTimeout(() => {
        setShowCursor(false);
        setCursorBlinking(false);
        setCursorPosition(0);

        currentMessageIndex.current++;
        displayNextMessage();
        ensureVisibility();
      }, cursorDuration);
    }

    function typeNextChar({ 
      fullText, 
      contentArray,
      currentPartIndex, 
      currentCharIndex, 
      speed, 
      cursorDuration,
      nonAnimatedText,
      bracketText = '', 
      containerClass = ''
    }) {
      const currentPart = contentArray[currentPartIndex];
      
      if (currentCharIndex >= fullText.length) {
        // Done typing - build final HTML
        const finalHtml = nonAnimatedText + 
          buildHtml(contentArray) + 
          bracketText;
        
        setDisplayedContent(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: finalHtml,
            dots: '',
            type: 'stream',
            containerClass,
            useHtml: true
          };
          return updated;
        });
        
        finishTyping(cursorDuration);
        return;
      }

      // Update display with current typing progress
      setDisplayedContent(prev => {
        const updated = [...prev];
        let visibleChars = 0;
        let htmlContent = nonAnimatedText;
        
        for (let i = 0; i < contentArray.length; i++) {
          const part = contentArray[i];
          if (typeof part.text !== 'string') continue;
          
          const partLength = part.text.length;
          
          if (visibleChars + partLength <= currentCharIndex) {
            htmlContent += part.className ? 
              `<span class="${part.className}">${part.text}</span>` : 
              part.text;
            visibleChars += partLength;
          } else {
            const charsToShow = Math.max(0, currentCharIndex - visibleChars);
            if (charsToShow > 0) {
              const visibleText = part.text.substring(0, charsToShow);
              htmlContent += part.className ?
                `<span class="${part.className}">${visibleText}</span>` :
                visibleText;
            }
            break;
          }
        }
        
        // Add bracket text if we're done typing dots
        if (currentCharIndex >= fullText.length && bracketText) {
          htmlContent += bracketText;
        }
        
        updated[updated.length - 1] = {
          text: htmlContent,
          dots: '',
          type: 'stream',
          useHtml: true,
          containerClass
        };
        return updated;
      });

      setCursorPosition(prev => prev + 1);
      currentCharIndex++;
      ensureVisibility();

      // Check if we've reached the end of the current part
      let accumulatedLength = 0;
      for (let i = 0; i <= currentPartIndex; i++) {
        accumulatedLength += contentArray[i].text.length;
        if (currentCharIndex === accumulatedLength && i === currentPartIndex) {
          if (i + 1 < contentArray.length) {
            setCursorBlinking(true);
            typingIntervalRef.current = setTimeout(() => {
              setCursorBlinking(false);
              typeNextChar({
                fullText,
                contentArray,
                currentPartIndex: i + 1,
                currentCharIndex,
                speed,
                cursorDuration,
                nonAnimatedText,
                bracketText,
                containerClass
              });
            }, currentPart.delay || MIN_DELAY);
            return;
          }
          finishTyping(cursorDuration);
          return;
        }
      }
      
      // Continue typing
      typingIntervalRef.current = setTimeout(() => {
        typeNextChar({
          fullText,
          contentArray,
          currentPartIndex,
          currentCharIndex,
          speed,
          cursorDuration,
          nonAnimatedText,
          bracketText,
          containerClass
        });
      }, speed);
    }

    function clearAllAndType(contentArray, speed, cursorDuration, showPrefix, className = '', containerClass = '') {
      clearAllTimeouts();
      setIsTyping(true);
      setShowCursor(true);
      setCursorBlinking(false);

      const isSpecialMessage = contentArray.some(part => 
        typeof part.text === 'string' && (
          part.text.includes('Welcome, unauthorized user') ||
          part.text.includes('Had to make a run for it.') ||
          part.text.includes('Back again? Good.') ||
          part.text.includes('This chair\'s developing')
        )
      );

      // Handle special messages
      if (isSpecialMessage) {
        setDisplayedContent(prev => [...prev, { text: '', dots: '', type: 'stream', useHtml: true, containerClass }]);
        
        const fullText = contentArray.reduce((acc, part) => 
          acc + (typeof part.text === 'string' ? part.text : ''), '');
        
        setTimeout(() => {
          typeNextChar({
            fullText,
            contentArray,
            currentPartIndex: 0,
            currentCharIndex: 0,
            speed,
            cursorDuration,
            nonAnimatedText: '',
            bracketText: '',
            containerClass
          });
          ensureVisibility();
        }, 300);
        return;
      }

      // Handle dot and bracket restructuring
      const restructured = restructureContentForDots(contentArray);
      
      if (restructured) {
        const { initialParts, animatedParts, bracketParts } = restructured;
        const fullText = animatedParts.reduce((acc, part) => acc + part.text, '');
        const initialHtml = buildHtml(initialParts, showPrefix ? "Union: " : "");
        const bracketHtml = buildHtml(bracketParts);
        
        setDisplayedContent(prev => [...prev, { text: initialHtml, dots: '', type: 'stream', useHtml: true, containerClass }]);
        
        setTimeout(() => {
          typeNextChar({
            fullText,
            contentArray: animatedParts,
            currentPartIndex: 0,
            currentCharIndex: 0,
            speed,
            cursorDuration,
            nonAnimatedText: initialHtml,
            bracketText: bracketHtml,
            containerClass
          });
          ensureVisibility();
        }, 500);
        return;
      }
      
      // Normal handling
      const { animatedParts, nonAnimatedHtml } = contentArray.reduce((acc, part) => {
        if (shouldAnimatePart(part)) {
          acc.animatedParts.push(part);
        } else if (typeof part.text === 'string') {
          acc.nonAnimatedHtml += part.className ? 
            `<span class="${part.className}">${part.text}</span>` : 
            part.text;
        }
        return acc;
      }, { animatedParts: [], nonAnimatedHtml: showPrefix ? "Union: " : "" });
      
      const fullText = animatedParts.reduce((acc, part) => acc + part.text, '');

      setDisplayedContent(prev => [...prev, { text: nonAnimatedHtml, dots: '', type: 'stream', useHtml: Boolean(nonAnimatedHtml), containerClass }]);

      if (fullText.length > 0) {
        setTimeout(() => {
          typeNextChar({
            fullText,
            contentArray: animatedParts,
            currentPartIndex: 0,
            currentCharIndex: 0,
            speed,
            cursorDuration,
            nonAnimatedText: nonAnimatedHtml,
            bracketText: '',
            containerClass
          });
          ensureVisibility();
        }, 500);
      } else {
        finishTyping(cursorDuration);
      }
    }

    function addProcessingIndicator(duration, nextType) {
      const steps = Math.floor(duration / DOT_ANIMATION_INTERVAL);
      for (let i = 0; i < steps; i++) {
        const dotCount = i + 1;
        setTimeout(() => {
          setDisplayedContent(prev => {
            const updated = [...prev];
            if (updated.length && updated[updated.length - 1].text) {
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                dots: '.'.repeat(dotCount)
              };
            }
            return updated;
          });
          ensureVisibility();
        }, i * DOT_ANIMATION_INTERVAL);
      }
      
      setTimeout(() => {
        setDisplayedContent(prev => {
          const updated = [...prev];
          if (updated.length && updated[updated.length - 1].text) {
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              dots: ''
            };
          }
          return updated;
        });
        setShowCursor(nextType === 'stream');
        setCursorPosition(0);
        ensureVisibility();
      }, duration - 50);
    }

    function displayNextMessage() {
      if (currentMessageIndex.current >= initialMessages.length) return;

      const message = initialMessages[currentMessageIndex.current];
      
      // Check for CONTINUE button
if (message.type === 'button' && typeof message.content === 'string' && 
    message.content.includes(CONTINUE_BUTTON_TEXT)) {
  setDisplayedContent(prev => [...prev, { 
    text: `<a href="/pages/archive.php" class="continue-button"><span class="continue-button__main"><span class="continue-button__bracket-left">[</span><span class="continue-button__text">CONTINUE TO ARCHIVE</span><span class="continue-button__bracket-right">]</span></span><span class="continue-button__glitch continue-button__glitch--1"><span>[</span><span>CONTINUE TO ARCHIVE</span><span>]</span></span><span class="continue-button__glitch continue-button__glitch--2"><span>[</span><span>CONTINUE TO ARCHIVE</span><span>]</span></span></a>`, 
    dots: '', 
    type: 'button',
    useHtml: true,
    className: message.className || '',
    containerClass: message.containerClass || ''
  }]);
  setShowCursor(false);
  setTimeout(ensureVisibility, 200);
  currentMessageIndex.current++;
  return;
}
      
      if (message.type === 'stream') {
        setIsTyping(true);
        setShowCursor(true);
        setCursorPosition(0);

        const contentArray = Array.isArray(message.content) ? 
          message.content.map(part => 
            typeof part === 'string' ? { text: part, delay: 0 } :
            typeof part === 'object' && part.text ? { ...part, className: part.className || '' } :
            { text: '', delay: 0 }
          ) : 
          [{ text: message.content, delay: 0 }];

        clearAllAndType(
          contentArray,
          DEFAULT_TYPING_SPEED,
          message.cursorDuration || DEFAULT_CURSOR_DURATION,
          message.showPrefix,
          message.className || '',
          message.containerClass || ''
        );
      } else {
        // Handle non-stream content
        let content = message.content;
        let useHtml = false;
        
        if (message.type === 'bracket' && Array.isArray(message.content)) {
          content = message.content.reduce((html, part) => {
            if (typeof part === 'object' && typeof part.text === 'string') {
              return html + (part.className ? 
                `<span class="${part.className}">${part.text}</span>` : part.text);
            }
            return html + (typeof part === 'string' ? part : '');
          }, '');
          useHtml = true;
        }
        
        setDisplayedContent(prev => [...prev, { 
          text: content, 
          dots: '', 
          type: message.type,
          useHtml,
          className: message.className || '',
          containerClass: message.containerClass || '' 
        }]);
        
        setShowCursor(false);
        setTimeout(ensureVisibility, 200);
        
        if (currentMessageIndex.current < initialMessages.length - 1 && 
            message.type !== 'image') {
          addProcessingIndicator(
            message.delay,
            initialMessages[currentMessageIndex.current + 1].type
          );
        }
        
        currentMessageIndex.current++;
        setTimeout(displayNextMessage, message.delay);
      }
    }

    displayNextMessage();

    return () => clearAllTimeouts();
  }, [initialMessages]);

  return (
    <div className={theme}>
      <div className="movement-overlay"></div>
      <div className="grain-overlay"></div>
      <Terminal
        ref={terminalRef}
        content={displayedContent}
        showCursor={showCursor}
        isTyping={isTyping}
        cursorPosition={cursorPosition}
        cursorBlinking={cursorBlinking}
      />
    </div>
  );
}